import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useModerationContext from '../../../context/useModerationContext';
import { ModerationFlowsModel, ModerationAttendee } from '../../../models';

import { ContextMenu, Button, DropdownItem, ToastContext } from '@weezevent/nacre';
import NavTitle from './NavTitle';

export default function ModerationContextMenu({ organization, moderationFlowId }) {
    const { t } = useTranslation();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [moderationContext, setModerationContext] = useModerationContext();
    const [nextAttendeeLoading, setNextAttendeeLoading] = React.useState(false);

    ModerationFlowsModel.useApiModel(
        {
            id: moderationFlowId,
            organizationId: organization.id,
            launch: Boolean(moderationFlowId),
            cache: false,
            allow_cache: false,
            onSuccess: response => {
                const query = response.allow_removed_attendees ? { include_removed: true } : {};
                setModerationContext({
                    flow: response,
                    removedAttendeesQuery: query
                });
            }
        },
        [organization.id, moderationFlowId]
    );

    const [flowsData, loadingFlowsData] = ModerationFlowsModel.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            allow_cache: false
        },
        [organization, moderationFlowId]
    );

    const flowsList = React.useMemo(() => {
        if (!flowsData || loadingFlowsData) {
            return [];
        }
        return flowsData;
    }, [flowsData, loadingFlowsData]);

    const handleNextAttendee = React.useCallback(() => {
        setNextAttendeeLoading(true);
        ModerationAttendee.getNextAttendee({
            organizationId: organization.id,
            flowId: moderationFlowId,
            query: window.location.search
        })
            .then(response => {
                history.push(response.url);
            })
            .catch(() => {
                toast.error(t('ticket.moderation.attendee.nexAttendeeError'));
            })
            .finally(() => {
                setNextAttendeeLoading(false);
            });
    }, [organization, moderationFlowId]);

    return (
        <ContextMenu
            catchphrase={<NavTitle title={t('ticket.moderation.contextMenu.linkBack')} link={`/ticket/O${organization.id}/moderation`} />}
            title={moderationContext.flow?.name || ' '}
            cta={
                <Button fluid primary disabled={nextAttendeeLoading} onClick={() => handleNextAttendee()}>
                    {t('ticket.moderation.contextMenu.startModerationCTA')}
                </Button>
            }
            switchMenuProps={{
                footerText: t('ticket.moderation.contextMenu.switchFooter'),
                onFooterClick: () => history.push(`/ticket/O${organization.id}/moderation`),
                text: t('ticket.moderation.contextMenu.switchPlaceholder'),
                items: flowsList.map(flow => (
                    <DropdownItem key={flow.id} onClick={() => history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${flow.id}/moderation_attendees`)}>
                        {flow.name}
                    </DropdownItem>
                ))
            }}
        />
    );
}
